import axios from 'axios';

import { BASE_URL } from '../service_urls';

// Alert Vault Jira integration (/v1/alert-vault/jira/*). These routes are
// newer than the published @ariksa/alert-vault client, so this module calls
// them directly with the same base URL and bearer token, and adapts the
// legacy notification request shapes the existing Jira screens send.

const jiraHttp = () =>
  axios.create({
    baseURL: BASE_URL,
    headers: {
      Authorization: `Bearer ${sessionStorage.getItem('authentication') ?? ''}`,
    },
  });

// Finding rows carry "finding:<group>" (or "policy:<id>") group ids; the API
// takes the raw PolicyHub group id.
export const findingGroupId = (groupId?: string | null) =>
  groupId ? String(groupId).replace(/^(finding|policy):/, '') : undefined;

const ticketScope = (notificationFor?: string | null) =>
  notificationFor === 'group_alerts' ? 'finding' : 'alert';

// Legacy Jira client config keys that are credentials, not settings.
const credentialKeys = ['token', 'user', 'url'];

const jiraClientBody = (clientConfig: any) => {
  const jira = clientConfig?.jira ?? {};
  const config: Record<string, unknown> = {};
  Object.entries(jira).forEach(([key, value]) => {
    if (!credentialKeys.includes(key) && key !== 'include_details') {
      config[key] = value;
    }
  });
  return {
    name: clientConfig?.name,
    description: clientConfig?.description ?? undefined,
    status: clientConfig?.status ?? true,
    config,
    credentials: { url: jira.url, user: jira.user, token: jira.token },
  };
};

export const AlertVaultJiraService = {
  // ClientsApiGetJiraProjectsRequest: projects for unsaved credentials.
  projectsForCredentials: (request: any) => {
    const body = request?.jiraClientRequest ?? request;
    return jiraHttp().post('/v1/alert-vault/jira/projects', body);
  },
  // ClientsApiGetJiraProjectRequest: projects of a saved client.
  clientProjects: (request: any) =>
    jiraHttp().get(
      `/v1/alert-vault/jira/clients/${
        request?.clientId ?? request?.uuid
      }/projects`,
    ),
  // ClientsApiValidateClientConfigRequest: legacy validation created a test
  // issue, so this does too.
  validate: (request: any) =>
    jiraHttp().post('/v1/alert-vault/jira/clients/validate', {
      ...jiraClientBody(request?.clientConfig),
      create_test_ticket: true,
    }),
  // ClientsApiAddClientRequest for a Jira client.
  createClient: (request: any) =>
    jiraHttp().post(
      '/v1/alert-vault/jira/clients',
      jiraClientBody(request?.clientConfig),
    ),
  // ClientsApiEditClientRequest for a Jira client; an empty token keeps the
  // stored one.
  updateClient: (request: any) => {
    const body = jiraClientBody(
      request?.editClientConfig ?? request?.clientConfig,
    );
    if (!body.credentials.token) body.credentials.token = undefined;
    return jiraHttp().put(
      `/v1/alert-vault/jira/clients/${request?.uuid}`,
      body,
    );
  },
  // ClientsApiGetClientTicketFieldsRequest: the dynamic ticket form.
  ticketFields: (request: any) =>
    jiraHttp().get(
      `/v1/alert-vault/jira/clients/${request?.clientId}/ticket-fields`,
      {
        params: {
          scope: ticketScope(request?.notificationFor),
          alert_id: request?.alertUuid ?? undefined,
          group_id: findingGroupId(request?.groupUuid),
          issue_type: request?.issueType ?? undefined,
        },
      },
    ),
  // ClientsApiSearchJiraFieldRequest: suggestions for a searchable field.
  search: (request: any) =>
    jiraHttp().get(`/v1/alert-vault/jira/clients/${request?.clientId}/search`, {
      params: {
        field: request?.field,
        query: request?.query,
        project: request?.project ?? undefined,
        issue_type_id: request?.issueTypeId ?? undefined,
      },
    }),
  // ClientsApiCreateTicketRequest: creates the Jira issue; the response keeps
  // the legacy { jira: <key> } field.
  createTicket: (request: any) => {
    const ticket = request?.ticketCreateRequest ?? request;
    return jiraHttp().post('/v1/alert-vault/jira/tickets', {
      client_id: ticket?.client_id,
      scope: ticketScope(ticket?.notification_for),
      alert_id: ticket?.alert_uuid ?? undefined,
      group_id: findingGroupId(ticket?.group_uuid),
      fields: ticket?.ticket_create_fields ?? {},
    });
  },
  // Sync one ticket (Alert Vault ticket id) or all tickets of a client; the
  // response is the legacy BulkTicketSyncResponse shape.
  sync: (ticketId?: string, clientId?: string) =>
    ticketId
      ? jiraHttp().post(
          `/v1/alert-vault/jira/tickets/${ticketId}/synchronizations`,
        )
      : jiraHttp().post('/v1/alert-vault/jira/tickets/synchronizations', null, {
          params: { client_id: clientId ?? undefined },
        }),
  // Legacy PUT /clients/ticket/{alert_id}?ticket_status=: moves the alert's
  // Jira ticket(s) to To Do / In Progress / Done.
  setAlertTicketStatus: async (alertId: string, status: string) => {
    const http = jiraHttp();
    const tickets = await http.get('/v1/alert-vault/tickets', {
      params: { alert_id: alertId, limit: 100 },
    });
    const items: any[] = tickets.data?.items ?? [];
    const results = await Promise.all(
      items
        .filter(ticket => ticket.external_id)
        .map(ticket =>
          http.put(`/v1/alert-vault/jira/tickets/${ticket.ticket_id}/status`, {
            status,
          }),
        ),
    );
    return { ...tickets, data: results.map(result => result.data) };
  },
};

// Tickets of one finding group: the finding ticket and its sub-tasks.
export const listFindingTickets = (groupId: string) =>
  jiraHttp().get('/v1/alert-vault/tickets', {
    params: { group_id: groupId, limit: 100 },
  });

// Ticket status of many alerts in one request, in the legacy alert-logs
// shape ([{ alert_id, logs: [{ action, ticket_status, ticket_id }] }]) the
// Issues table renders. Batches of 200 match the API limit.
export const alertTicketLogs = async (alertIds: string[]) => {
  const http = jiraHttp();
  const batches: string[][] = [];
  for (let i = 0; i < alertIds.length; i += 200) {
    batches.push(alertIds.slice(i, i + 200));
  }
  const pages = await Promise.all(
    batches.map(ids =>
      http.get('/v1/alert-vault/tickets', {
        params: { alert_ids: ids.join(','), limit: 100 },
      }),
    ),
  );
  const logs: Record<string, any[]> = {};
  pages.forEach(page =>
    (page.data?.items ?? []).forEach((ticket: any) => {
      if (!ticket.external_id) return;
      (logs[ticket.alert_id] = logs[ticket.alert_id] ?? []).push({
        action: 'jira',
        ticket_id: ticket.external_id,
        ticket_status: ticket.status,
        browse_link: ticket.browse_link,
      });
    }),
  );
  return {
    data: Object.entries(logs).map(([alert_id, entries]) => ({
      alert_id,
      logs: entries,
    })),
  };
};

export const isJiraClient = (request: any) =>
  String(
    request?.clientConfig?.client_name ??
      request?.editClientConfig?.client_name ??
      request?.clientName ??
      '',
  ).toLowerCase() === 'jira';
